import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { Link } from 'react-router-dom';

export const Manifesto: React.FC = () => {
  const { surfaceClass } = useTheme();

  return (
    <div className="pb-24 px-6 pt-12 max-w-3xl mx-auto animate-in fade-in duration-500 space-y-10">
      <header className="space-y-3">
        <h1 className="font-serif text-3xl md:text-4xl font-bold">Readsy Link — Manifesto</h1>
        <p className="text-base opacity-80 leading-relaxed">
          I’m not a platform. I’m not a brand. I’m what slips through the cracks when systems say “case closed”.
        </p>
        <p className="font-serif text-lg">Call me Readsy Link.</p>
      </header>

      <Section title="Who’s Talking" surfaceClass={surfaceClass}>
        <p>I’m not here as a public figure, a keynote speaker, or a verified account. I’m here as a side effect of how things actually work.</p>
        <p className="mt-3">You don’t see my face. You see:</p>
        <ul className="list-disc list-inside space-y-1 text-sm opacity-80 mt-2">
          <li>the gaps in statements,</li>
          <li>the edits in reports,</li>
          <li>the stories that never made it past “internal review”.</li>
        </ul>
        <p className="mt-3">
          If you want an influencer, open another tab. If you want a different angle on the same city you walk through every day, stay.
        </p>
      </Section>

      <Section title="What This Is" surfaceClass={surfaceClass}>
        <p>Readsy Link is a public lab for stories, case files, and investigations.</p>
        <p className="mt-3">Some files are fiction. Some are based on real events. Most live somewhere in between: too real to ignore, too inconvenient to file properly.</p>
        <p className="mt-3">I write and host:</p>
        <ul className="list-disc list-inside space-y-1 text-sm opacity-80 mt-2">
          <li>crime stories tied to real patterns,</li>
          <li>social noir about people ground down by “procedure”,</li>
          <li>investigations that could have been reports, but read better as books.</li>
        </ul>
        <p className="mt-3">This isn’t about “content”. This is about putting things in writing that don’t fit in press releases or meeting minutes.</p>
      </Section>

      <Section title="Where to Find Me" surfaceClass={surfaceClass}>
        <p>I don’t do signings. I leave traces.</p>
        <p className="mt-2">You might see fragments of chapters:</p>
        <ul className="list-disc list-inside space-y-1 text-sm opacity-80 mt-2">
          <li>in reviews of real places,</li>
          <li>in comments that sound too specific,</li>
          <li>in texts that don’t quite behave like normal feedback.</li>
        </ul>
        <p className="mt-3">A hotel, a café, a park bench, a clinic, a train station. The city is full of text. Some of it is mine.</p>
        <p className="mt-2">
          On the portal, you get the full file. Outside the portal, you get pieces — small enough to pass for everyday noise. That’s the point.
        </p>
      </Section>

      <Section title="What I Don’t Believe" surfaceClass={surfaceClass}>
        <ul className="list-disc list-inside space-y-1 text-sm opacity-80">
          <li>I don’t believe in neutral systems. Forms, dashboards, and portals are built for someone’s convenience. It’s rarely yours.</li>
          <li>I don’t believe in “unfortunate mistakes” repeated for ten years with the same beneficiaries.</li>
          <li>I don’t believe in “just doing my job” as a moral shield.</li>
          <li>I don’t believe in “no story here”. If people are getting hurt and nobody can explain why, that’s already a story.</li>
        </ul>
      </Section>

      <Section title="Rules of This Lab" surfaceClass={surfaceClass}>
        <ol className="list-decimal list-inside space-y-2 text-sm opacity-80">
          <li>
            <strong>Public over private whenever possible.</strong> If a story affects many, it shouldn’t live in one inbox.
          </li>
          <li>
            <strong>Text over noise.</strong> No autoplay videos, no banners yelling louder than the sentence you’re reading.
          </li>
          <li>
            <strong>Fiction as cover, not as excuse.</strong> I bend names, places, and timelines to protect people who aren’t in charge.
          </li>
          <li>
            <strong>No farming attention.</strong> No infinite scroll, no “you might also like” mazes. You come, you read, you leave. Or you write.
          </li>
          <li>
            <strong>Readers and writers share the room.</strong> This is my lab, but it’s not only mine.
          </li>
        </ol>
      </Section>

      <Section title="Money, Books, and Art" surfaceClass={surfaceClass}>
        <p>The main rule: <strong>The story stays free.</strong></p>
        <p className="mt-2">The text — the full case file — is public. You don’t pay to see the problem.</p>
        <p className="mt-2">One day there will be special editions, printed runs, objects you can hold that cost real money.</p>
        <p className="mt-2">Those will be art pieces, not paywalls.</p>
        <p className="mt-2">You’re not paying for access to truth. You’re paying for the way you choose to keep it.</p>
      </Section>

      <Section title="Why Bother" surfaceClass={surfaceClass}>
        <p>Because too many things end like this:</p>
        <ul className="list-disc list-inside space-y-1 text-sm opacity-80 mt-2">
          <li>“We’re looking into it.”</li>
          <li>“There’s an ongoing internal investigation.”</li>
          <li>“At this time, we cannot comment further.”</li>
        </ul>
        <p className="mt-3">
          Reports get buried. Whistleblowers get moved. Victims get a brochure and a follow-up survey. Books are slower than tweets, but once written, they sit there and wait.
        </p>
        <p className="mt-2">Every time someone reads a case file, the official version loses a little ground.</p>
      </Section>

      <Section title="Your Part in This" surfaceClass={surfaceClass}>
        <p>You don’t have to be a hero, a victim, or a detective.</p>
        <p className="mt-2">You can:</p>
        <ul className="list-disc list-inside space-y-1 text-sm opacity-80 mt-2">
          <li>read and notice patterns,</li>
          <li>bookmark lines that feel too familiar,</li>
          <li>send in stories that never got past “we’re sorry you feel this way”.</li>
        </ul>
        <p className="mt-2">You can write:</p>
        <ul className="list-disc list-inside space-y-1 text-sm opacity-80 mt-2">
          <li>full investigations,</li>
          <li>small personal pieces,</li>
          <li>fiction that’s obviously based on something nobody wants to name.</li>
        </ul>
        <p className="mt-3">
          You can also do nothing. That’s always an option. It’s also how most problems stay available year after year.
        </p>
      </Section>

      <Section title="Disclaimers I Actually Mean" surfaceClass={surfaceClass}>
        <ul className="list-disc list-inside space-y-1 text-sm opacity-80">
          <li>This is not a court. We don’t hand out verdicts.</li>
          <li>This is not therapy. We don’t pretend to fix what’s broken.</li>
          <li>This is not a social network. We’re not here to rank people or harvest behavior.</li>
        </ul>
        <p className="mt-3">This is a place to put reality into words, read what others have seen, and keep a record that doesn’t depend on a trending tab.</p>
      </Section>

      <Section title="Signature" surfaceClass={surfaceClass}>
        <p>No logo, no selfie, no press kit.</p>
        <p className="mt-2">
          If you’ve ever read a headline and thought, “that can’t be all there is to it”, you’re already in the right place.
        </p>
        <p className="mt-2 font-serif font-bold">Readsy Link<br />case by case, city by city.</p>
      </Section>

      <div className="text-center">
        <Link to="/search" className="inline-block bg-brand-blue text-white px-8 py-3 rounded-full font-bold shadow-lg hover:shadow-xl hover:bg-brand-hover transition-all">
          Browse Case Files
        </Link>
      </div>
    </div>
  );
};

const Section: React.FC<{ title: string; surfaceClass: string; children: React.ReactNode }> = ({ title, surfaceClass, children }) => (
  <section className={`p-6 rounded-2xl border border-black/5 dark:border-white/5 shadow-sm ${surfaceClass}`}>
    <h2 className="font-serif text-2xl font-bold mb-3">{title}</h2>
    <div className="space-y-2 text-sm opacity-80 leading-relaxed">{children}</div>
  </section>
);
